<script setup>
import { useI18n } from "vue-i18n"
import { ref } from "vue"
import axios from "axios"
import { apiUrl, defaultImage, formatCurrency } from "@common/helpers"
import qs from "qs"

const { t } = useI18n()
const showSubmenuRef = ref(false)
const hoveredCategory = ref(0)
const hoveredSubCategory = ref(null)
const hoveredSubCategorySubmenu = ref(false)
const loadingProducts = ref(false)
const loadingSubcategoryProducts = ref(false)
const products = ref([])

const getApiParams = (param) => {
  const apiParams = {
    panels: {
      searchAttribute: "panelSearchAttributes",
      collections: "PANELS",
      categories: "HARDWARE",
    },
    panelsAccessories: {
      searchAttribute: "accessorySearchAttributes",
      collections: "PANELS_ACCESSORIES",
      categories: "HARDWARE",
    },
    inverters: {
      searchAttribute: "inverterSearchAttributes",
      collections: "INVERTERS",
      categories: "HARDWARE",
    },
    invertersMonophase: {
      searchAttribute: "inverterSearchAttributes",
      collections: "INVERTERS_MONOPHASE",
      categories: "HARDWARE",
    },
    invertersTriphaseLte30kw: {
      searchAttribute: "inverterSearchAttributes",
      collections: "INVERTERS_TRIPHASE_LTE_30KW",
      categories: "HARDWARE",
    },
    invertersTriphaseGt30kw: {
      searchAttribute: "inverterSearchAttributes",
      collections: "INVERTERS_TRIPHASE_GT_30KW",
      categories: "HARDWARE",
    },
    invertersHybrid: {
      searchAttribute: "inverterSearchAttributes",
      collections: "INVERTERS_HYBRID",
      categories: "HARDWARE",
    },
    invertersAccessories: {
      searchAttribute: "accessorySearchAttributes",
      collections: "INVERTERS_ACCESSORIES",
      categories: "HARDWARE",
    },
    batteries: {
      searchAttribute: "batterySearchAttributes",
      collections: "BATTERIES",
      categories: "HARDWARE",
    },
    batteryAccessories: {
      searchAttribute: "accessorySearchAttributes",
      collections: "BATTERY_ACCESSORIES",
      categories: "HARDWARE",
    },
    disconnects: {
      searchAttribute: "disconnectSearchAttributes",
      collections: "DISCONNECTS",
      categories: "HARDWARE",
    },
    servicePanels: {
      searchAttribute: "servicePanelSearchAttributes",
      collections: "SERVICE_PANELS",
      categories: "HARDWARE",
    },
    workwear: {
      searchAttribute: "workwearSearchAttributes",
      collections: "WORKWEAR",
      categories: "HARDWARE",
    },
    mountingSystems: {
      searchAttribute: "mountingSystemsSearchAttributes",
      collections: "MOUNTING_SYSTEMS",
      categories: "HARDWARE",
    },
  }

  return apiParams[param]
}

// Sidebar categories
const navigationItems = [
  // panels
  {
    display: true,
    label: t("common.api.products.collections.secondary-menubar.PANELS"),
    isActive: false,
    hasSubmenu: true,
    categoryLink: "/hardware/shop/panels",
    params: getApiParams("panels"),
    filters: [
      {
    label: t("common.api.products.collections.secondary-menubar.BY_RATING_STC"),
        hasSubmenu: true,
        subcategories: [
          {
            label: "300w - 400w",
            filters: [
              { key: "ratingStcGte", value: "300" },
              { key: "ratingStcLte", value: "339" },
            ],
          },
          {
            label: "400w - 450w",
            filters: [
              { key: "ratingStcGte", value: "400" },
              { key: "ratingStcLte", value: "449" },
            ],
          },
          {
            label: "450w - 500w",
            filters: [
              { key: "ratingStcGte", value: "450" },
              { key: "ratingStcLte", value: "499" },
            ],
          },
          {
            label: "500w+",
            filters: [{ key: "ratingStcGte", value: "500" }],
          },
        ],
      },
      {
        label: t("common.api.products.collections.secondary-menubar.BY_TECHNOLOGY"),
        hasSubmenu: true,
        subcategories: [
          {
            label: "Monocrystallins",
            filters: [{ key: "type", value: "MONOCRYSTALLINS" }],
          },
          { label: "CIGS", filters: [{ key: "type", value: "CIGS" }] },
          {
            label: "Polycrystallins",
            filters: [{ key: "type", value: "POLYCRYSTALLINS" }],
          },
        ],
      },
      {
        label:  t("common.api.products.collections.secondary-menubar.TOP_BRANDS"),
        function: "loadTopBrands",
        collections: "PANELS",
        isCustom: true,
        hasSubmenu: true,
        subcategories: [],
      },
    ],
  },
  // inverters
  {
    display: true,
    label: t("common.api.products.collections.secondary-menubar.INVERTERS"),
    isActive: false,
    hasSubmenu: true,
    categoryLink: "/hardware/shop/inverters",
    params: getApiParams("inverters"),
    id: "inverters",
    filters: [
      {
        label:  t("common.api.products.collections.secondary-menubar.MICROINVERTERS"),
        filters: [{ key: "isMicroinverter", value: true }],
      },
      { label: "Triphase", filters: [{ key: "phases", value: 3 }] },
      { label: "Monophase", filters: [{ key: "phases", value: 1 }] },
      {
            label: t("common.api.products.collections.secondary-menubar.DC_OPTIMISERS"),
            filters: [{ key: "DC_OPTIMISER", value: true }],
          },
      {
        label:  t("common.api.products.collections.secondary-menubar.TOP_BRANDS"),
        function: "loadTopBrands",
        isCustom: true,
        collections: "INVERTERS",
        hasSubmenu: true,
        subcategories: [],
      },
    ],
  },
  // batteries
  {
    display: true,
    label: t("common.api.products.collections.secondary-menubar.BATTERIES"),
    isActive: false,
    hasSubmenu: true,
    categoryLink: "/hardware/shop/batteries",
    params: getApiParams("batteries"),
    id: "batteries",
    filters: [
      {
        label: "Lithium Ion batteries",
        filters: [{ key: "type", value: "LITHIUM_IRON_PHOSPHATE" }],
      },
      {
        label: "Batteries < 5kWh",
        filters: [{ key: "capacityUsableLte", value: "5000" }],
      },
      {
        label: "Batteries 5-10 kWh",
        filters: [
          { key: "capacityUsableGte", value: 5000 },
          { key: "capacityUsableLte", value: 10000 },
        ],
      },
      {
        label: "Batteries 10kWh+",
        filters: [{ key: "capacityUsableGte", value: 10000 }],
      },
      {
        label: "Accessories",
        hasSubmenu: false,
        isCustom: true,
        function: "loadBatteryAccessories",
      },
    ],
  },
]

// this is used to debounce the loading of the products
let debounceTimer = null

const loadProducts = async (item, type, noDefaults = false) => {
  if (loadingProducts.value) return

  if (!noDefaults) {
    loadingProducts.value = true
    showSubmenuRef.value = true

    // clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    // this is used to debounce the loading of the products
    debounceTimer = setTimeout(async () => {
      products.value = []

      // load all the products for the category
      if (type === "category") {
        // load products from the api
        const { searchAttribute, ...params } = item.params

        const response = await axios.get(`/products/search`, {
          params,
          paramsSerializer: (p) => qs.stringify(p, { allowDots: true }),
        })

        products.value = response.data.items
      }

      // load the products for the subcategory
      if (type === "subcategory") {
        const { searchAttribute, ...base } = item.item.params
        const params = {
          ...base,
          [searchAttribute]: Object.fromEntries(
            item.filters.filters.map((filter) => [filter.key, filter.value]),
          ),
        }

        const response = await axios.get(`/products/search`, {
          params,
          paramsSerializer: (p) => qs.stringify(p, { allowDots: true }),
        })

        products.value = response.data.items
      }

      // load the products for the battery accessories
      if (type === "BATTERY_ACCESSORIES") {
        const response = await axios.get(`/products/search`, {
          params: {
            categories: "HARDWARE",
            collections: "BATTERY_ACCESSORIES",
          },
        })

        products.value = response.data.items
        loadingSubcategoryProducts.value = false
      }

      // load the products for the this brand
      if (type === "THIS_BRAND_PRODUCTS") {
        console.log(item)

        const params = {
          categories: "HARDWARE",
          collections: item.collections,
          ...Object.fromEntries(
            item.filters.map((filter) => [filter.key, filter.value]),
          ),
        }

        const response = await axios.get(`/products/search`, {
          params,
          paramsSerializer: (p) => qs.stringify(p, { allowDots: true }),
        })

        products.value = response.data.items
        loadingSubcategoryProducts.value = false
      }

      loadingProducts.value = false
    }, 500)
  } else {
    // clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    // this is used to debounce the loading of the products
    debounceTimer = setTimeout(async () => {
      // load the products for the top brands
      if (type === "TOP_BRANDS") {
        if (
          navigationItems[hoveredCategory.value].filters[
            hoveredSubCategory.value
          ].subcategories.length > 0
        ) {
          loadingSubcategoryProducts.value = false
          return
        }

        loadingSubcategoryProducts.value = true

        const response = await axios.get("/hardware/manufacturers/search", {
          params: {
            categories: item.collections,
            collections: "TOP_BRANDS",
            featuredForPanels: item.collections === "PANELS" ? true : false,
            featuredForInverters:
              item.collections === "INVERTERS" ? true : false,
            featuredForBatteries:
              item.collections === "BATTERIES" ? true : false,
          },
        })

        let subcategories = response.data.items.map((currItem) => ({
          label: currItem.companyName,
          isCustom: true,
          function: "loadThisBrandProducts",
          collections: item.collections,
          filters: [{ key: "manufacturers", value: currItem.id }],
        }))

        navigationItems[hoveredCategory.value].filters[
          hoveredSubCategory.value
        ].subcategories = subcategories

        loadingSubcategoryProducts.value = false
      }
    }, 500)
  }
}

const showMenu = (item, index) => {
  navigationItems[index].isActive = true
  hoveredCategory.value = index
  hoveredSubCategory.value = null
  hoveredSubCategorySubmenu.value = false
  loadProducts(item, "category")
}

const showSubmenu = (filter, index) => {
  if (filter.isCustom && filter.function === "loadBatteryAccessories") {
    loadProducts(filter, "BATTERY_ACCESSORIES")
  }

  if (filter.isCustom && filter.function === "loadTopBrands") {
    loadingSubcategoryProducts.value = true
    loadProducts(filter, "TOP_BRANDS", true)
  }

  hoveredSubCategory.value = index
  hoveredSubCategorySubmenu.value = filter.hasSubmenu ? true : false
}

const exitSubmenu = () => {
  hoveredCategory.value = null
  hoveredSubCategory.value = null
  hoveredSubCategorySubmenu.value = false
}

const loadSubcategory = (filters, item) => {
  if (filters.isCustom && filters.function === "loadThisBrandProducts") {
    loadProducts(filters, "THIS_BRAND_PRODUCTS")
  }

  loadProducts({ item, filters }, "subcategory")
}

const getQuantity = (product) => {
  if (!product.supplierListingPriceRange) return 0

  return Math.max(
    product.supplierListingPriceRange.max?.limit ?? 0,
    product.supplierListingPriceRange.min?.limit ?? 0,
  )
}
</script>

<template>
  <div class="bg-white" @mouseleave="exitSubmenu">
    <!-- Header Navigation -->
    <div class="border-b border-gray-200 bg-white">
      <div class="lg:px-20 py-3">
        <!-- Left Navigation -->
        <nav class="flex w-full items-center overflow-x-auto space-x-2">
          <!-- Shop All -->
          <RouterLink
            class="flex cursor-pointer text-nowrap items-center rounded-md px-4 py-2 text-gray-700 duration-100 hover:bg-gray-200 hover:text-gray-900"
            to="/hardware/shop/manufacturers"
          >
            <svg
              class="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
            {{
              $t("common.api.products.collections.secondary-menubar.SHOP_ALL")
            }}
          </RouterLink>

          <button
            v-for="(item, index) in navigationItems.filter(
              (item) => item.display,
            )"
            :key="index"
            :class="{ 'bg-gray-200 text-gray-900': hoveredCategory === index }"
            class="flex cursor-pointer text-nowrap items-center rounded-md px-4 py-2 text-gray-700 duration-100 hover:bg-gray-200 hover:text-gray-900"
            @mouseenter="showMenu(item, index)"
          >
            {{ item.label }}
            <svg
              v-if="item.hasSubmenu"
              class="ml-1 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </button>
        </nav>
      </div>
    </div>

    <!-- hover options -->
    <div
      v-if="showSubmenuRef && hoveredCategory !== null"
      class="border-b border-gray-200 shadow-lg"
    >
      <div class="pl-20">
        <div class="grid grid-cols-[260px_1fr]">
          <div class="relative border-r border-gray-200 bg-white">
            <div
              class="absolute inset-0 z-20 flex flex-col space-y-1 bg-white py-3 pr-3"
            >
              <h3 class="mb-2 pl-4 text-lg font-semibold text-gray-900 before:mb-2 before:block before:h-[4px] before:w-16 before:bg-orange-400 before:content-['']">
                {{ navigationItems[hoveredCategory].label }}
              </h3>

              <template
                v-for="(filter, index) in navigationItems[hoveredCategory]
                  .filters"
                :key="filter.label"
              >
                <button
                  class="mt-1 flex cursor-pointer items-center justify-between rounded-md px-5 py-3.5 text-base font-medium text-black duration-100 hover:bg-gray-100"
                  @mouseleave="loadingSubcategoryProducts = false"
                  @mouseenter="showSubmenu(filter, index)"
                >
                  <span>{{ filter.label }}</span>
                  <svg
                    v-if="filter.hasSubmenu"
                    class="h-4 w-4 -rotate-90"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </button>
              </template>
            </div>

            <!-- submenu submenu -->
            <div
              :class="hoveredSubCategorySubmenu ? 'left-[261px]' : 'left-0'"
              class="absolute overflow-y-auto top-0 bottom-0 z-10 flex w-[240px] flex-col space-y-1 border-r border-gray-200 bg-white px-2 py-6 duration-100"
              @mouseleave="hoveredSubCategorySubmenu = false"
            >
              <div class="ml-4 h-1 w-10 rounded-full bg-orange-600"></div>
              <h3 class="mb-2 pl-4 text-xl font-semibold text-gray-900">
                {{
                  navigationItems[hoveredCategory]?.filters[hoveredSubCategory]
                    ?.label
                }}
              </h3>

              <!-- loading -->
              <div v-if="loadingSubcategoryProducts" class="space-y-2">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="min-w-[220px] space-y-2 animate-pulse rounded-md border border-gray-200 bg-white p-2 shadow-sm"
                >
                  <div class="w-full h-3 rounded-full bg-gray-200"></div>
                </div>
              </div>

              <template
                v-else
                v-for="(item) in navigationItems[hoveredCategory]
                  ?.filters[hoveredSubCategory]?.subcategories || []"
                :key="index"
              >
                <button
                  class="mt-1 flex cursor-pointer items-center justify-between rounded-md px-4 py-2 text-base font-medium text-black duration-100 hover:bg-gray-100"
                  @mouseenter="
                    loadSubcategory(item, navigationItems[hoveredCategory])
                  "
                >
                  <span>{{ item.label }}</span>
                </button>
              </template>
            </div>
          </div>
          <!-- Product Grid -->
          <div
            :class="{ 'pl-[248px]': hoveredSubCategorySubmenu }"
            class="overflow-hidden bg-gray-50 p-4"
          >
            <div
              v-if="products.length > 0 || loadingProducts"
              class="flex gap-4 overflow-x-auto pb-2"
            >
              <!-- Loading products -->
              <template v-if="loadingProducts">
                <div
                  v-for="i in 3"
                  :key="i"
                  class="min-w-[220px] rounded-md border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div
                    class="mb-3 h-6 w-20 animate-pulse rounded-full bg-gray-200"
                  ></div>
                  <div class="mb-4 flex justify-center">
                    <div
                      class="size-24 animate-pulse rounded-md bg-gray-200"
                    ></div>
                  </div>
                  <div class="space-y-2">
                    <div
                      class="h-3 w-16 animate-pulse rounded-full bg-gray-200"
                    ></div>
                    <div
                      class="h-4 w-32 animate-pulse rounded-full bg-gray-200"
                    ></div>
                    <div
                      class="h-3 w-24 animate-pulse rounded-full bg-gray-200"
                    ></div>
                  </div>
                </div>
              </template>
              <!-- Products -->
              <template v-else>
                <template v-for="item in products" :key="item.name">
                  <RouterLink
                    @click="exitSubmenu"
                    :to="{
                      name: 'HardwareMarketplaceShopProduct',
                      params: { slug: item.slug },
                    }"
                  >
                    <div
                      class="min-w-[220px] cursor-pointer hover:bg-gray-50 duration-150 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <!-- Quantity badge -->
                      <div
                        class="mb-3 flex w-fit items-center rounded-full border border-gray-300 px-2 py-1"
                      >
                        <div class="h-2 w-2 rounded-full bg-green-500"></div>
                        <span class="ml-2 text-xs text-gray-600">
                          {{
                            $t(
                              "common.api.products.collections.secondary-menubar.AVAILABLE",
                            )
                          }}
                          {{ getQuantity(item) }}
                          {{
                            $t(
                              "common.api.products.collections.secondary-menubar.PCS",
                            )
                          }}
                        </span>
                      </div>

                      <!-- Product image -->
                      <div class="mb-4 flex justify-center">
                        <img
                          class="w-16 h-[88px] object-contain rounded-md"
                          :src="
                            item.thumbnailImage
                              ? apiUrl(item.thumbnailImage.thumbnail.url)
                              : defaultImage
                          "
                          alt=""
                        />
                      </div>

                      <!-- Product info -->
                      <div class="space-y-2">
                        <h4
                          class="text-xs leading-tight font-medium text-gray-900"
                        >
                          {{ item.title }}
                        </h4>
                        <p
                          class="text-xs text-gray-600"
                          v-if="getQuantity(item)"
                        >
                          {{
                            $t(
                              "common.api.products.collections.secondary-menubar.FROM",
                            )
                          }}
                          <span class="font-medium">
                            {{
                              formatCurrency(
                                item.supplierListingPriceRange?.min
                                  ?.pricePerUnit / 100,
                              )
                            }}
                          </span>
                        </p>
                        <p class="text-xs text-red-500" v-else>
                          {{
                            $t(
                              "common.api.products.collections.secondary-menubar.OUT_OF_STOCK",
                            )
                          }}
                        </p>
                      </div>
                    </div>
                  </RouterLink>
                </template>
              </template>
            </div>
            <!-- No products found -->
            <div v-else class="flex h-full items-center justify-center py-28">
              <p class="text-sm text-gray-500">
                {{
                  $t(
                    "common.api.products.collections.secondary-menubar.NO_PRODUCTS_FOUND",
                  )
                }}
              </p>
            </div>

            <!-- Show All Products Link -->
            <div class="mt-4 text-center">
              <RouterLink
                :to="navigationItems[hoveredCategory]?.categoryLink"
                class="block cursor-pointer items-center rounded-md bg-gray-200/25 p-3 text-center text-sm font-medium text-gray-700 duration-100 hover:bg-gray-200"
              >
                {{
                  $t(
                    "common.api.products.collections.secondary-menubar.SHOW_ALL_PRODUCTS",
                  )
                }}
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
